import { ProductType, ProductRedeemFeeType } from 'src/type/product';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { convertGold } from 'src/utils/convert';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';

const Schema = Yup.object().shape({
  name: Yup.string().required('กรุณากรอกชื่อ'),
  description: Yup.string(),
  weightGram: Yup.number()
    .typeError('น้ำหนักต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุน้ำหนักกรัม')
    .moreThan(0, 'จำนวนต้องมากกว่า 0'),
  fee: Yup.number()
    .typeError('ค่าธรรมเนียมต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุค่าธรรมเนียม')
    .moreThan(-1, 'จำนวนขั้นต่ำ 0'),
});

const defaultValues = {
  name: '',
  description: '',
  weightGram: 0,
  weightBaht: 0,
  fee: 0,
};

type Props = {
  open: boolean;
  onClose: VoidFunction;
  product: ProductType;
  productFee: ProductRedeemFeeType | null;
  handleReload: VoidFunction;
};

export default function ProductFeeFormDialog({
  open,
  onClose,
  product,
  productFee,
  handleReload,
}: Props) {
  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const handleNumberInput =
    (field: 'weightGram' | 'fee') => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value: originalValue } = event.target;
      let value = originalValue;
      if (value && value.startsWith('.')) {
        value = `0${value}`;
      }
      setValue(field, value as unknown as number, {
        shouldValidate: true,
        shouldDirty: true,
      });

      if (field === 'weightGram') {
        const numeric = Number(value) || 0;
        const weightBaht = convertGold(numeric, 'weightBaht').toFixed(4);
        // update via reset to avoid strict typing on setValue keys
        reset((prev: any) => ({
          ...prev,
          weightBaht,
        }));
      }
    };

  useEffect(() => {
    if (open && productFee) {
      reset({
        name: productFee.name,
        description: productFee.description,
        fee: productFee.fee && productFee.fee > 0 ? productFee.fee : 0,
        weightGram: productFee.weightGram && productFee.weightGram > 0 ? productFee.weightGram : 0,
        weightBaht: productFee.weightBaht && productFee.weightBaht > 0 ? productFee.weightBaht : 0,
      } as any);
    }
  }, [productFee?.id, open, reset]);

  const onSubmit = handleSubmit(async (values: any) => {
    try {
      const body = {
        productId: product.id,
        name: values.name,
        description: values.description,
        weightGram: Number(values.weightGram),
        weightBaht: Number(values.weightBaht),
        fee: Number(values.fee),
        currency: 'THB',
      };

      if (productFee) {
        await axios.patch(endpoints.productRedeemFees.update(productFee.id), body);
      } else {
        await axios.post(endpoints.productRedeemFees.post, body);
      }
      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{productFee ? 'แก้ไข' : 'สร้าง'}ค่าธรรมเนียม</DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            <RHFTextField name="name" placeholder="ชื่อ" InputLabelProps={{ shrink: true }} />

            <RHFTextField
              multiline
              rows={3}
              name="description"
              placeholder="รายละเอียด"
              InputLabelProps={{ shrink: true }}
            />

            <Stack spacing={2} direction="row" alignItems="center">
              <RHFTextField
                name="weightGram"
                type="number"
                label="น้ำหนักกรัม"
                placeholder="0"
                InputLabelProps={{ shrink: true }}
                onChange={handleNumberInput('weightGram')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography fontWeight="bold" color="success.main">
                        กรัม
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="body2" color="text.secondary">
                =
              </Typography>
              <RHFTextField
                name="weightBaht"
                type="number"
                label="น้ำหนักบาท"
                placeholder="0"
                InputLabelProps={{ shrink: true }}
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography fontWeight="bold" color="success.main">
                        บาท
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <RHFTextField
              name="fee"
              type="number"
              label="ค่าธรรมเนียม"
              placeholder="0"
              InputLabelProps={{ shrink: true }}
              onChange={handleNumberInput('fee')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography fontWeight="bold" color="success.main">
                      THB
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
